import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Collapse,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import EditIcon from '@mui/icons-material/Edit';
import {
  getSavedFilters,
  deleteSavedFilter,
  toggleFavorite as toggleListCleanerFavorite,
  updateSavedFilter
} from '../utils/savedFiltersUtils';
import {
  getResearchTemplates,
  deleteResearchTemplate,
  toggleResearchTemplateFavorite,
  updateResearchTemplate
} from '../utils/researchTemplatesUtils';

// Row styling constants (matching Activity History)
const rowStyles = {
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  borderRadius: 3,
  p: 2,
  mb: 1,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
  }
};

const headerRowStyles = {
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
  border: '1px solid rgba(255, 255, 255, 0.05)',
  borderRadius: 2,
  p: 2,
  mb: 1
};

/**
 * TemplatesPage component - unified view for both List Cleaner and Research Agent templates
 * Styled to match Activity History table design
 */
const TemplatesPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // List Cleaner templates state
  const [listCleanerTemplates, setListCleanerTemplates] = useState([]);
  const [loadingListCleaner, setLoadingListCleaner] = useState(true);

  // Research templates state
  const [researchTemplates, setResearchTemplates] = useState([]);
  const [loadingResearch, setLoadingResearch] = useState(true);

  // Common state
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Load templates on mount
  useEffect(() => {
    loadListCleanerTemplates();
    loadResearchTemplates();
  }, []);

  // Load List Cleaner templates
  const loadListCleanerTemplates = async () => {
    try {
      setLoadingListCleaner(true);
      const templates = await getSavedFilters();
      setListCleanerTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Error loading List Cleaner templates:', error);
      setError('Failed to load List Cleaner templates');
    } finally {
      setLoadingListCleaner(false);
    }
  };

  // Load Research templates
  const loadResearchTemplates = async () => {
    try {
      setLoadingResearch(true);
      const templates = await getResearchTemplates();
      setResearchTemplates(Array.isArray(templates) ? templates : []);
    } catch (error) {
      console.error('Error loading Research templates:', error);
      setError('Failed to load Research templates');
    } finally {
      setLoadingResearch(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchTerm('');
    setExpandedTemplate(null);
  };

  // Handle delete for List Cleaner
  const handleDeleteListCleaner = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteSavedFilter(templateId);
      setListCleanerTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (error) {
      setError('Failed to delete template');
    }
  };

  // Handle delete for Research
  const handleDeleteResearch = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteResearchTemplate(templateId);
      setResearchTemplates(prev => prev.filter(t => t.id !== templateId));
    } catch (error) {
      setError('Failed to delete template');
    }
  };

  // Handle favorite toggle for List Cleaner
  const handleToggleListCleanerFavorite = async (templateId) => {
    try {
      const result = await toggleListCleanerFavorite(templateId);
      if (result?.filter) {
        setListCleanerTemplates(prev =>
          prev.map(t => t.id === templateId ? { ...t, is_favorite: result.filter.is_favorite } : t)
        );
      }
    } catch (error) {
      setError('Failed to update favorite status');
    }
  };

  // Handle favorite toggle for Research
  const handleToggleResearchFavorite = async (templateId) => {
    try {
      const result = await toggleResearchTemplateFavorite(templateId);
      if (result?.template) {
        setResearchTemplates(prev =>
          prev.map(t => t.id === templateId ? { ...t, is_favorite: result.template.is_favorite } : t)
        );
      }
    } catch (error) {
      setError('Failed to update favorite status');
    }
  };

  // Start editing
  const handleStartEdit = (template, type) => {
    setExpandedTemplate(template.id);
    if (type === 'listCleaner') {
      const columnConfig = template.filter_config?.columnConfig || {};
      const columnInstructions = {};
      Object.keys(columnConfig).forEach(col => {
        columnInstructions[col] = columnConfig[col]?.instructions || '';
      });
      setEditForm({
        name: template.name,
        description: template.description || '',
        columnInstructions
      });
    } else {
      setEditForm({
        name: template.name,
        description: template.description || '',
        instructions: template.instructions || ''
      });
    }
  };

  // Save edit for List Cleaner
  const handleSaveListCleanerEdit = async (templateId) => {
    try {
      const template = listCleanerTemplates.find(t => t.id === templateId);
      const existingColumnConfig = template?.filter_config?.columnConfig || {};
      const updatedColumnConfig = { ...existingColumnConfig };

      if (editForm.columnInstructions) {
        Object.keys(editForm.columnInstructions).forEach(col => {
          if (updatedColumnConfig[col]) {
            updatedColumnConfig[col] = {
              ...updatedColumnConfig[col],
              instructions: editForm.columnInstructions[col]
            };
          }
        });
      }

      const updateData = {
        name: editForm.name,
        description: editForm.description,
        filterConfig: {
          ...template?.filter_config,
          columnConfig: updatedColumnConfig
        }
      };

      await updateSavedFilter(templateId, updateData);
      setListCleanerTemplates(prev =>
        prev.map(t => t.id === templateId ? {
          ...t,
          name: editForm.name,
          description: editForm.description,
          filter_config: {
            ...t.filter_config,
            columnConfig: updatedColumnConfig
          }
        } : t)
      );
      setExpandedTemplate(null);
    } catch (error) {
      setError('Failed to save changes');
    }
  };

  // Save edit for Research
  const handleSaveResearchEdit = async (templateId) => {
    try {
      await updateResearchTemplate(templateId, editForm);
      setResearchTemplates(prev =>
        prev.map(t => t.id === templateId ? { ...t, ...editForm } : t)
      );
      setExpandedTemplate(null);
    } catch (error) {
      setError('Failed to save changes');
    }
  };

  // Filter and sort templates
  const filterAndSort = (templates) => {
    const filtered = templates.filter(t =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    return filtered.sort((a, b) => {
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    });
  };

  const sortedListCleaner = filterAndSort(listCleanerTemplates);
  const sortedResearch = filterAndSort(researchTemplates);

  // Render a List Cleaner template row
  const renderListCleanerRow = (template) => (
    <Paper key={template.id} sx={rowStyles}>
      {/* Main row content */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <IconButton
            size="small"
            onClick={() => handleToggleListCleanerFavorite(template.id)}
            sx={{ mr: 1.5, color: template.is_favorite ? '#F7BF50' : 'rgba(255,255,255,0.5)' }}
          >
            {template.is_favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: '#0095FF' }}>
              {template.name}
            </Typography>
            {template.description && expandedTemplate !== template.id && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {template.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleStartEdit(template, 'listCleaner')}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#0095FF' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteListCleaner(template.id)}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#FF1493' } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded edit panel */}
      <Collapse in={expandedTemplate === template.id}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={editForm.name || ''}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={editForm.description || ''}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={2}
          />

          {editForm.columnInstructions && Object.keys(editForm.columnInstructions).length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#0095FF' }}>
                Column Instructions
              </Typography>
              {Object.keys(editForm.columnInstructions).map(col => (
                <TextField
                  key={col}
                  label={col}
                  value={editForm.columnInstructions[col] || ''}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    columnInstructions: {
                      ...editForm.columnInstructions,
                      [col]: e.target.value
                    }
                  })}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setExpandedTemplate(null)}>Cancel</Button>
            <Button size="small" variant="contained" onClick={() => handleSaveListCleanerEdit(template.id)}>
              Save
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );

  // Render a Research template row
  const renderResearchRow = (template) => (
    <Paper key={template.id} sx={rowStyles}>
      {/* Main row content */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <IconButton
            size="small"
            onClick={() => handleToggleResearchFavorite(template.id)}
            sx={{ mr: 1.5, color: template.is_favorite ? '#F7BF50' : 'rgba(255,255,255,0.5)' }}
          >
            {template.is_favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: '#faa32c' }}>
              {template.name}
            </Typography>
            {template.description && expandedTemplate !== template.id && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {template.description}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleStartEdit(template, 'research')}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#faa32c' } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteResearch(template.id)}
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#FF1493' } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expanded edit panel */}
      <Collapse in={expandedTemplate === template.id}>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={editForm.name || ''}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            size="small"
            fullWidth
          />
          <TextField
            label="Description"
            value={editForm.description || ''}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Research Instructions"
            value={editForm.instructions || ''}
            onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={4}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setExpandedTemplate(null)}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: '#faa32c', '&:hover': { bgcolor: '#e8941f' } }}
              onClick={() => handleSaveResearchEdit(template.id)}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );

  // Render empty state
  const renderEmptyState = (type) => (
    <Paper sx={{ ...headerRowStyles, p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
      <Typography variant="h6" gutterBottom>
        No {type === 'listCleaner' ? 'List Cleaner' : 'Website Analyzer'} templates
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {searchTerm
          ? 'No templates match your search.'
          : `Save a template from the ${type === 'listCleaner' ? 'List Cleaner' : 'Website Analyzer'} results to see it here.`}
      </Typography>
      {searchTerm && (
        <Button variant="text" onClick={() => setSearchTerm('')} sx={{ mt: 2 }}>
          Clear Search
        </Button>
      )}
    </Paper>
  );

  const isLoading = activeTab === 0 ? loadingListCleaner : loadingResearch;
  const currentTemplates = activeTab === 0 ? sortedListCleaner : sortedResearch;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ p: 3, width: '100%', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tabs - no icons, matching Activity History */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="LIST CLEANER" />
            <Tab label="WEBSITE ANALYZER" />
          </Tabs>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
            }}
          />
        </Box>

        {/* Content */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <CircularProgress />
          </Box>
        ) : currentTemplates.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {activeTab === 0
              ? currentTemplates.map(t => renderListCleanerRow(t))
              : currentTemplates.map(t => renderResearchRow(t))}
          </Box>
        ) : (
          renderEmptyState(activeTab === 0 ? 'listCleaner' : 'research')
        )}
      </Paper>
    </Box>
  );
};

export default TemplatesPage;
